/**
 * Les conversations copilotées d'une entreprise, sur sa fiche.
 *
 * Les appels étaient empilés à plat, du plus récent au plus ancien, sans dire
 * lesquels se répondaient. Or on ne relit pas un appel : on relit un fil — de
 * quoi on était parti, ce qui s'est dit depuis, où on en est. D'où cette vue :
 *
 *   - une carte par conversation, jamais deux fils mélangés ;
 *   - l'objectif qui a lancé la prise de contact, en tête ;
 *   - la chronologie des appels de ce fil, numérotés, chacun dépliable jusqu'à
 *     la transcription intégrale ;
 *   - trois notes — le client, le commercial, leur moyenne — et la posture du
 *     prospect vis-à-vis de notre solution.
 *
 * Le regroupement et les notes viennent du serveur (crm.copilot.threads) :
 * l'affichage ne décide rien, il montre. Une lecture au montage, aucun
 * rafraîchissement automatique : ces échanges sont clos par définition.
 * L'appel en cours, lui, s'affiche dans le panneau Copilot.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, ChevronDown, PhoneCall, Sparkles, Clock, Target,
  TrendingUp, TrendingDown, CircleDot, CheckCircle2, PhoneOff,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";

const fmtDuration = (s) => (!s ? "—"
  : `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`);

const fmtTotal = (s) => (!s ? "—" : s < 60 ? `${s} s` : `${Math.round(s / 60)} min`);

const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })
  : "—");

const fmtDay = (iso) => (iso
  ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })
  : "—");

const OUTCOME_STYLE = {
  MEETING: "bg-emerald-50 text-emerald-700",
  INTERESTED: "bg-emerald-50 text-emerald-700",
  CALLBACK: "bg-amber-50 text-amber-700",
  NOT_INTERESTED: "bg-rose-50 text-rose-600",
  NO_ANSWER: "bg-slate-100 text-slate-600",
  WRONG_NUMBER: "bg-slate-100 text-slate-600",
};

// Où en est le client vis-à-vis de notre solution — l'information qui dit quoi
// faire au prochain appel, donc celle qui mérite d'être lisible de loin.
const POSTURE_STYLE = {
  EXPANSION: "bg-indigo-50 text-indigo-700 ring-indigo-100",
  AFFILIE: "bg-violet-50 text-violet-700 ring-violet-100",
  SATISFAIT: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  DECOUVERTE: "bg-sky-50 text-sky-700 ring-sky-100",
  RETICENT: "bg-rose-50 text-rose-600 ring-rose-100",
  INCONNU: "bg-slate-100 text-slate-500 ring-slate-200",
};

const RESULT_STYLE = {
  ACQUIS: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  POTENTIEL: "bg-amber-50 text-amber-700 ring-amber-100",
  AUCUN: "bg-slate-100 text-slate-500 ring-slate-200",
};

const scoreTone = (n) => (n >= 70 ? "emerald" : n >= 50 ? "amber" : "rose");

const BAR = { emerald: "bg-emerald-500", amber: "bg-amber-500", rose: "bg-rose-500" };
const TEXT = { emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-600" };

/** Une note sur 100, barre comprise. `null` se dit « non noté », pas « zéro ». */
function Score({ label, value, strong = false }) {
  if (value === null || value === undefined) {
    return (
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-[.08em] text-slate-400">{label}</div>
        <div className="mt-1 text-[13px] text-slate-400">non noté</div>
      </div>
    );
  }
  const tone = scoreTone(value);
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-[.08em] text-slate-400">{label}</span>
        <span className={`font-mono ${strong ? "text-[15px] font-semibold" : "text-[13px]"} ${TEXT[tone]}`}>
          {value}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${BAR[tone]}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

/** Les trois notes du fil, et son sens de marche. */
function ScoreBoard({ scores }) {
  const trend = scores?.trend;
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3">
      <div className="mb-2.5 flex items-center justify-between">
        <span className="text-[12.5px] font-medium text-slate-600">Qualité de l'échange</span>
        {trend !== null && trend !== undefined && trend !== 0 && (
          <span className={`flex items-center gap-1 font-mono text-[11.5px] ${trend > 0 ? "text-emerald-700" : "text-rose-600"}`}>
            {trend > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {trend > 0 ? `+${trend}` : trend} depuis le 1<sup>er</sup> appel
          </span>
        )}
      </div>
      <div className="flex items-start gap-4">
        <Score label="Client" value={scores?.client} />
        <Score label="Commercial" value={scores?.commercial} />
        <Score label="Moyenne" value={scores?.average} strong />
      </div>
      <p className="mt-2 text-[11.5px] text-slate-400 text-pretty">
        {scores?.calls_scored > 0
          ? `Moyenne sur ${scores.calls_scored} appel${scores.calls_scored > 1 ? "s" : ""} noté${
              scores.calls_scored > 1 ? "s" : ""} — seuls les appels où client et commercial se sont parlé sont notés.`
          : "Aucun appel noté : il faut que le client et le commercial se soient parlé pour qu'il y ait une qualité d'échange."}
      </p>
    </div>
  );
}

function Badge({ className = "", children }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${className}`}>
      {children}
    </span>
  );
}

/** Correction du résultat d'un appel : le commercial une fois, le manager sans limite. */
function OutcomeEditor({ call, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(call.outcome || "");
  const [saving, setSaving] = useState(false);
  const options = call.outcome_options || [];
  const save = async () => {
    if (!value || value === call.outcome) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/copilot/conversations/calls/${call.session_id}/outcome`, { outcome: value });
      toast.success("Résultat de l'appel modifié");
      setEditing(false);
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 text-[12.5px]"
      data-testid="outcome-editor">
      <span className="text-slate-500">Résultat :</span>
      {editing ? (
        <>
          <select data-testid="outcome-select" value={value} onChange={(e) => setValue(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-[12.5px]">
            {!call.outcome && <option value="">— Choisir —</option>}
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button data-testid="outcome-save" disabled={saving} onClick={save}
            className="rounded-md bg-indigo-600 px-2.5 py-1 font-medium text-white hover:bg-indigo-700 disabled:opacity-50">Enregistrer</button>
          <button onClick={() => setEditing(false)} className="text-slate-500 hover:text-slate-800">Annuler</button>
        </>
      ) : (
        <>
          <span className={`rounded-full px-2 py-0.5 font-medium ${OUTCOME_STYLE[call.outcome] || "bg-slate-100 text-slate-600"}`}>
            {call.outcome_label || "non renseigné"}
          </span>
          {call.outcome_edit && (
            <span className="text-slate-400" data-testid="outcome-edited">
              modifié par {call.outcome_edit.by_name || "—"}
              {call.outcome_edit.at && ` le ${fmtDate(call.outcome_edit.at)}`}
              {call.outcome_edit.previous_label && ` (était : ${call.outcome_edit.previous_label})`}
            </span>
          )}
          {call.can_edit_outcome ? (
            <button data-testid="outcome-edit" onClick={() => { setValue(call.outcome || ""); setEditing(true); }}
              className="ml-auto text-indigo-600 hover:underline">Modifier le résultat</button>
          ) : (
            <span className="ml-auto text-slate-400" title={call.outcome_edit_refusal || ""} data-testid="outcome-locked">
              modification déjà utilisée
            </span>
          )}
        </>
      )}
    </div>
  );
}

/** Un appel dans la chronologie du fil : sa place, son verdict, sa transcription. */
function CallNode({ call, last, open, onToggle, closed, onOutcomeChanged }) {
  const scores = call.quality || {};
  const silent = call.outcome === "NO_ANSWER" || call.outcome === "WRONG_NUMBER";
  return (
    <div className="relative pl-9" data-testid="conversation-call">
      {!last && <span className="absolute left-[13px] top-7 bottom-0 w-px bg-slate-200" />}
      <span className={`absolute left-0 top-1 flex h-[26px] w-[26px] items-center justify-center rounded-full font-mono text-[11px] ring-4 ring-white ${
        silent ? "bg-slate-100 text-slate-500" : "bg-slate-900 text-white"}`}>
        {call.call_number}
      </span>

      <button onClick={onToggle} className="w-full rounded-lg px-2 py-1.5 text-left hover:bg-slate-50/80">
        <span className="flex flex-wrap items-center gap-2">
          {silent ? <PhoneOff size={13} className="text-slate-400" />
            : <PhoneCall size={13} className="text-emerald-600" />}
          <span className="font-mono text-[12px] text-slate-500">{fmtDate(call.started_at || call.created_at)}</span>
          <span className="font-mono text-[11.5px] text-slate-400">
            <Clock size={11} className="mr-1 inline" />{fmtDuration(call.duration_seconds)}
          </span>
          {call.outcome && (
            <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-medium ${OUTCOME_STYLE[call.outcome] || "bg-slate-100 text-slate-600"}`}>
              {call.outcome_label || call.outcome}
            </span>
          )}
          {scores.average !== null && scores.average !== undefined && (
            <span title={`Client ${scores.client} · Commercial ${scores.commercial} · Moyenne ${scores.average}`}
              className={`font-mono text-[11.5px] ${TEXT[scoreTone(scores.average)]}`}>
              {scores.client}/{scores.commercial} → {scores.average}
            </span>
          )}
          <ChevronDown size={14} className={`ml-auto shrink-0 text-slate-400 ${open ? "rotate-180" : ""}`} />
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px] text-slate-400">
          {call.objective && (
            <span className="flex items-center gap-1"><Target size={11} />{call.objective}</span>
          )}
          {call.commercial_name && <span>· {call.commercial_name}</span>}
          <span>· {call.message_count} prise{call.message_count > 1 ? "s" : ""} de parole</span>
        </span>
        {call.summary && !open && (
          <span className="mt-1 block text-[13px] text-slate-500 line-clamp-2 text-pretty">{call.summary}</span>
        )}
      </button>

      {open && (
        <div className="mb-3 mt-1 rounded-xl border border-slate-100 bg-slate-50/50 p-3.5">
          {call.summary && (
            <p className="mb-3 text-[13px] leading-relaxed text-slate-600 text-pretty">{call.summary}</p>
          )}
          {/* Conversation fermée : le résultat reste corrigeable, une fois pour
              le commercial, une fois la modale de fin d'appel refermée. */}
          {closed && <OutcomeEditor call={call} onChanged={onOutcomeChanged} />}

          {scores.source === "aucune" && (
            <p className="mb-3 rounded-lg border border-slate-100 bg-white px-3 py-2 text-[12.5px] text-slate-500 text-pretty">
              Pas de note de qualité : {scores.no_score_reason || "aucun échange transcrit"}.
              Une qualité d'échange suppose un échange.
            </p>
          )}

          {(scores.client_reason || scores.commercial_reason) && (
            <div className="mb-3 grid gap-2 sm:grid-cols-2">
              {[["Client", scores.client, scores.client_reason],
                ["Commercial", scores.commercial, scores.commercial_reason]].map(([who, note, why]) => (
                <div key={who} className="rounded-lg border border-slate-100 bg-white p-2.5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] uppercase tracking-[.08em] text-slate-400">{who}</span>
                    {note !== null && note !== undefined && (
                      <span className={`font-mono text-[13px] ${TEXT[scoreTone(note)]}`}>{note}</span>
                    )}
                  </div>
                  {why && <p className="mt-1 text-[12px] text-slate-500 text-pretty">{why}</p>}
                </div>
              ))}
            </div>
          )}
          {scores.source === "heuristique" && (
            <p className="mb-3 text-[11.5px] text-slate-400">
              Notes calculées sur les faits de l'appel — le modèle n'a pas noté cet échange.
            </p>
          )}

          {call.messages.length === 0 ? (
            <p className="py-4 text-center text-[13px] text-slate-400">
              Aucune parole transcrite — la transcription était indisponible pendant cet appel.
            </p>
          ) : (
            <div className="space-y-2.5" data-testid="conversation-transcript">
              {call.messages.map((m, i) => {
                const isCustomer = m.speaker === "customer";
                return (
                  <div key={`${m.timestamp_ms}-${i}`}>
                    <div className={`mb-1 font-mono text-[10.5px] uppercase tracking-[.1em] ${isCustomer ? "text-sky-700" : "text-slate-400"}`}>
                      {isCustomer ? "Client" : "Commercial"}
                    </div>
                    <div className={`rounded-xl px-3 py-2 text-[13.5px] leading-relaxed ${isCustomer ? "bg-white text-slate-900 border border-slate-100" : "bg-slate-100/70 text-slate-600"}`}>
                      {m.text}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {call.suggestions?.length > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-3">
              <div className="mb-2 flex items-center gap-1.5 text-[12.5px] font-medium text-slate-600">
                <Sparkles size={13} className="text-indigo-600" /> Suggestions du copilote
              </div>
              <div className="space-y-1.5">
                {call.suggestions.map((sug, i) => (
                  <div key={i} className="flex items-start gap-2 text-[12.5px] text-slate-500">
                    <span className={`mt-0.5 shrink-0 rounded-md px-1.5 py-0.5 font-mono text-[10.5px] uppercase ${sug.status === "used" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {sug.status === "used" ? "utilisée" : sug.status || "générée"}
                    </span>
                    <span className="text-pretty">« {sug.text} »</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Une conversation : son objectif d'origine, son état, ses appels. */
function ConversationCard({ conv, open, onToggle, openCall, setOpenCall, onOutcomeChanged }) {
  const closed = conv.status === "closed";
  const period = conv.calls_count > 1
    ? `du ${fmtDay(conv.started_at)} au ${fmtDay(conv.last_call_at)}`
    : fmtDay(conv.started_at);

  return (
    <div data-testid="copilot-conversation"
      className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button onClick={onToggle} className="flex w-full items-start gap-3 p-3.5 text-left hover:bg-slate-50/70">
        <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          closed ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
          {closed ? <CheckCircle2 size={15} /> : <CircleDot size={15} />}
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[11px] uppercase tracking-[.1em] text-slate-400">
              Conversation {conv.index}
            </span>
            <span className="text-sm font-medium text-slate-900">
              {conv.objective || "Objectif non précisé"}
            </span>
            <Badge className={POSTURE_STYLE[conv.posture] || POSTURE_STYLE.INCONNU}>
              {conv.posture_label}
            </Badge>
            <Badge className={RESULT_STYLE[conv.result_state] || RESULT_STYLE.AUCUN}>
              {conv.result_label}
            </Badge>
          </span>

          <span className="mt-1.5 block font-mono text-[11.5px] text-slate-400">
            {conv.calls_count} appel{conv.calls_count > 1 ? "s" : ""} · {fmtTotal(conv.duration_seconds)} · {period}
            {conv.commercials?.length ? ` · ${conv.commercials.join(", ")}` : ""}
            {closed && conv.closed_reason ? ` · clos : ${conv.closed_reason}` : ""}
          </span>

          {conv.summary && !open && (
            <span className="mt-1.5 block text-[13px] text-slate-500 line-clamp-2 text-pretty">{conv.summary}</span>
          )}
        </span>

        {conv.scores?.average !== null && conv.scores?.average !== undefined && (
          <span className="mt-0.5 shrink-0 text-right"
            title={`Moyenne de la conversation — client ${conv.scores.client} · commercial ${conv.scores.commercial}`}>
            <span className={`block font-mono text-[17px] font-semibold leading-none ${TEXT[scoreTone(conv.scores.average)]}`}>
              {conv.scores.average}
            </span>
            <span className="mt-1 block font-mono text-[10.5px] text-slate-400">
              {conv.scores.client} / {conv.scores.commercial}
            </span>
          </span>
        )}
        <ChevronDown size={16} className={`mt-1 shrink-0 text-slate-400 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/40 p-3.5">
          {conv.calls[0]?.opened_because && (
            <p className="mb-3 text-[12px] text-slate-400">
              Nouvelle conversation — {conv.calls[0].opened_because}.
            </p>
          )}
          <ScoreBoard scores={conv.scores} />
          <div className="mt-4 space-y-1">
            {conv.calls.map((call, i) => (
              <CallNode key={call.session_id} call={call} last={i === conv.calls.length - 1}
                open={openCall === call.session_id} closed={closed} onOutcomeChanged={onOutcomeChanged}
                onToggle={() => setOpenCall(openCall === call.session_id ? null : call.session_id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CompanyConversations({ company }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openConv, setOpenConv] = useState(null);
  const [openCall, setOpenCall] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/copilot/conversations/${company.id}`);
      const conversations = payload.conversations || [];
      setData({ ...payload, conversations });
      // On ouvre la conversation en cours et son dernier appel : c'est ce qu'on
      // vient chercher neuf fois sur dix avant de rappeler.
      const first = conversations[0];
      setOpenConv(first?.conversation_id || null);
      setOpenCall(first?.calls?.[first.calls.length - 1]?.session_id || null);
    } catch (e) {
      setData({ conversations: [] });
      toast.error(apiError(e.response?.data?.detail) || "Conversations indisponibles");
    } finally { setLoading(false); }
  }, [company.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <p className="flex items-center justify-center gap-2 py-10 text-sm text-slate-400">
        <RefreshCw size={14} className="animate-spin" /> Chargement des conversations…
      </p>
    );
  }

  const conversations = data?.conversations || [];

  if (!conversations.length) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm text-slate-400">Aucune conversation enregistrée</p>
        <p className="mx-auto mt-1.5 max-w-[420px] text-[12.5px] text-slate-400">
          Les appels passés avec « Préparer mon appel » sont transcrits ici, en entier,
          dès que le copilote est actif.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5" data-testid="copilot-conversations">
      <div className="flex items-center justify-between">
        <p className="text-[12.5px] text-slate-500">
          {conversations.length} conversation{conversations.length > 1 ? "s" : ""} ·{" "}
          {data.calls_count} appel{data.calls_count > 1 ? "s" : ""}
        </p>
        <button onClick={load} className="flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-900">
          <RefreshCw size={13} /> Actualiser
        </button>
      </div>

      {data.truncated && (
        <p className="text-[11.5px] text-slate-400">
          Seuls les {data.calls_count} appels les plus récents sont affichés : les
          conversations plus anciennes peuvent être incomplètes.
        </p>
      )}

      {conversations.map((conv) => (
        <ConversationCard key={conv.conversation_id} conv={conv}
          open={openConv === conv.conversation_id}
          onToggle={() => setOpenConv(openConv === conv.conversation_id ? null : conv.conversation_id)}
          openCall={openCall} setOpenCall={setOpenCall} onOutcomeChanged={load} />
      ))}
    </div>
  );
}
